import { type FieldValues, type Path, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { UploadedFilesPreview } from "components/UploadedFilesPreview";
import { UploadFilesZone } from "components/UploadFilesZone";
import { getFilesWithinSizeLimit } from "utils/common";

import type { PreviewAttachment } from "components/UploadedFilesPreview";

export type AttachmentProps<TFieldValues extends FieldValues, TAttachment extends { url: string }> = {
  name: Path<TFieldValues>;
  hasError?: boolean;
  display?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  onUpload: (file: File) => Promise<TAttachment | null>;
  onPreviewAttachments: (attachments: TAttachment[]) => PreviewAttachment[];
};

export const FormAttachments = <TFieldValues extends FieldValues, TAttachment extends { url: string }>({
  name,
  display = true,
  hasError = false,
  isUploading = false,
  disabled = false,
  onUpload,
  onPreviewAttachments
}: AttachmentProps<TFieldValues, TAttachment>) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TFieldValues>();
  const { field } = useController({ name, control });

  const attachments: TAttachment[] = field.value ?? [];

  const handleFiles = async (files: File[]) => {
    const allowedFiles = getFilesWithinSizeLimit(files, t);
    if (!allowedFiles.length) return;

    const results = await Promise.all(allowedFiles.map(onUpload));
    const successful = results.filter(r => r !== null);
    if (!successful.length) return;

    field.onChange([...attachments, ...successful]);
  };

  const handleDelete = async (url: string) => {
    const attachment = attachments.find(a => a.url === url);
    if (!attachment) return;

    field.onChange(attachments.filter(a => a.url !== url));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(e.target.files ?? []));
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <>
      <Form.Field>
        <UploadFilesZone
          hasError={hasError}
          display={display}
          isUploading={isUploading}
          disabled={disabled}
          onFileInputChange={handleFileChange}
          onFileDragOver={e => e.preventDefault()}
          onFileDrop={handleDrop}
        />
      </Form.Field>
      <UploadedFilesPreview attachments={onPreviewAttachments(attachments)} onDeleteAttachment={handleDelete} disabled={disabled} />
    </>
  );
};
